/* eslint-disable react/require-default-props */
import {
  ChangeEventHandler,
  InputHTMLAttributes,
  MutableRefObject,
  ReactElement,
  Ref,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import cx from 'classnames';

import styles from './NumberInput.module.scss';
import { handleChange } from './utils/handleChange';

export interface NumberInputRef extends MutableRefObject<HTMLInputElement> {
  changeValue: (newValue: string) => void;
  getValue: () => string;
}

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  'data-testid': string;
  label?: string;
  addLeadingZeroForDecimal?: boolean;
  negativeValueAllowed?: boolean;
  numberType?: 'decimal' | 'natural';
  before?: string | ReactElement;
  after?: string | ReactElement;
  below?: string | ReactElement;
  errorMessage?: string | ReactElement;
  defaultValue?: number;
  hasError?: boolean;
  labelClassName?: string;
  belowClassName?: string;
  containerClassName?: string;
  min?: number;
  max?: number;
}

/**
 * Input of type number
 * @description Number input with default onchange handler that beautifies inserted value
 * @returns Input with forward ref. If you want to change value use ref and forms properly
 * @requires data-testid
 * @usage_in placeorder_v2/QuantityInput/QuantityInput.tsx
 */

const NumberInput = forwardRef(
  (
    {
      'data-testid': dataTestId,
      inputMode = 'decimal',
      placeholder = '',
      onChange = () => null,
      className = '',
      addLeadingZeroForDecimal = false,
      negativeValueAllowed = false,
      numberType = 'natural',
      label,
      before,
      after,
      errorMessage,
      below,
      defaultValue,
      hasError,
      labelClassName,
      belowClassName,
      min,
      max,
      containerClassName,
    }: Props,
    ref: Ref<HTMLInputElement>
  ) => {
    const inputRef = useRef(null);
    const callbackRef = useRef<ChangeEventHandler<HTMLInputElement>>(() => null);

    const callback = useCallback(
      e => {
        handleChange(
          e,
          { addLeadingZeroForDecimal, negativeValueAllowed, numberType },
          callbackRef.current
        );
      },
      [addLeadingZeroForDecimal, negativeValueAllowed, numberType]
    );

    useEffect(() => {
      callbackRef.current = onChange;
    }, [onChange]);

    useImperativeHandle(
      ref,
      () => {
        return {
          changeValue: newValue => {
            if (inputRef.current && !Number.isNaN(Number(newValue))) {
              inputRef.current.value = newValue;
            }
          },
          getValue: () =>
            inputRef.current ? Number(inputRef.current?.value ?? '-1') : -1,
        };
      },
      []
    );

    return (
      (<div className={cx(containerClassName)} data-palette="NumberInput">
        {label && (
          <label
            htmlFor={dataTestId}
            className={cx('m-0 mb-1', styles.label, labelClassName)}
            data-testid={`${dataTestId}-label`}
            data-palette="NumberInput">
            {label}
          </label>
        )}
        <div
          className={cx('flex-set align-items-center flex--wrap', styles.inputContainer, {
            [styles.inputError]: hasError,
          })}
        >
          {before}
          <input
            defaultValue={defaultValue}
            id={dataTestId}
            type="number"
            autoComplete="off"
            step="any"
            data-testid={dataTestId}
            name={dataTestId}
            ref={inputRef}
            inputMode={inputMode}
            placeholder={placeholder}
            onChange={callback}
            className={cx('flexed-item', styles.input, className)}
            min={min}
            max={max}
          />
          {after}
          {below && <div className={cx(styles.below, belowClassName)} data-palette="NumberInput">{below}</div>}
        </div>
        {errorMessage && (
          <div
            className={cx('flex-set align-items-center mt-1', styles.errorMessage)}
            data-palette="NumberInput">
            {errorMessage}
          </div>
        )}
      </div>)
    );
  }
);

NumberInput.displayName = 'StyledNumberInput';

export default memo(NumberInput);
